import Link from 'next/link';
import type { MenuProps } from 'antd';
import IntlMessages from '../Locale/IntlMessages';

export type CauHinhEmailMenuItem = Required<MenuProps>['items'][number] & {
  modulename?: string;
  checklicense?: boolean;
  alertDemo?: boolean;
  children?: CauHinhEmailMenuItem[];
};

type RouteKeys = { selected: string[]; open: string[] };

const VH_ROUTE_PREFIX = '/van-hoa';

/** Route cấu hình email phân hệ Văn hóa — luôn có tiền tố `/van-hoa`. */
const vhEmailHref = (path: string) => {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${VH_ROUTE_PREFIX}${normalized}`;
};

let vanHoaEmailRouteMap: Record<string, RouteKeys> = {};
let registerVanHoaRoutes = false;

const registerRoute = (pathname: string, key: string, openAncestors: string[]) => {
  if (!registerVanHoaRoutes) return;
  vanHoaEmailRouteMap[pathname] = { selected: [key], open: ['sub8', ...openAncestors] };
};

const templateLeaf = (
  key: string,
  href: string,
  text: string,
  openAncestors: string[],
  i18nId?: string,
  modulename = 'cauhinh_template',
): CauHinhEmailMenuItem => {
  registerRoute(href, key, openAncestors);
  return {
    key,
    modulename,
    checklicense: false,
    alertDemo: true,
    label: i18nId ? (
      <Link href={href}>
        <IntlMessages id={i18nId} text={text} />
      </Link>
    ) : (
      <Link href={href}>{text}</Link>
    ),
  };
};

/** Menu Cấu hình Email — phân hệ Văn hóa (theo cây thiết kế). */
export const buildVanHoaCauHinhEmailChildren = (): CauHinhEmailMenuItem[] => {
  vanHoaEmailRouteMap = {};
  registerVanHoaRoutes = true;
  const items: CauHinhEmailMenuItem[] = [
    templateLeaf(
      'email-logs',
      vhEmailHref('/email-logs'),
      'Email Logs',
      [],
      'bsc.label.nhatkyemail',
      'cauhinh',
    ),
    templateLeaf(
      'thoi-gian-gui-mail',
      vhEmailHref('/cau-hinh-email/thoi-gian-gui-mail'),
      'Thời gian gửi mail',
      [],
      'bsc.label.thoigianguimail',
      'cauhinh_thoigian',
    ),
    templateLeaf(
      'cau-hinh-smtp',
      vhEmailHref('/cau-hinh-email/cau-hinh-smtp'),
      'Cấu hình SMTP',
      [],
      'bsc.menu.cau-hinh-smtp',
      'cauhinh_smtp',
    ),
    templateLeaf(
      'email-template',
      vhEmailHref('/cau-hinh-email/mau-mail'),
      'Email Template',
      [],
      undefined,
      'cauhinh_template,cauhinh_maukhoiphucmatkhau,cauhinh_maukhoiphucmatkhauthanhcong,cauhinh_maunhapbaocao',
    ),
  ];
  registerVanHoaRoutes = false;
  return items;
};

/** Menu Cấu hình Email — phân hệ BSC. */
export const buildBscCauHinhEmailChildren = (): CauHinhEmailMenuItem[] => [
  templateLeaf(
    'thoi-gian-gui-mail',
    '/cau-hinh-email/thoi-gian-gui-mail',
    'Thời gian gửi mail',
    [],
    'bsc.label.thoigianguimail',
    'cauhinh_thoigian',
  ),
  templateLeaf(
    'cau-hinh-smtp',
    '/cau-hinh-email/cau-hinh-smtp',
    'Cấu hình SMTP',
    [],
    'bsc.menu.cau-hinh-smtp',
    'cauhinh_smtp',
  ),
  templateLeaf(
    'email-template',
    '/cau-hinh-email/mau-mail',
    'Email Template',
    [],
    undefined,
    'cauhinh_template,cauhinh_maukhoiphucmatkhau,cauhinh_maukhoiphucmatkhauthanhcong,cauhinh_maunhapbaocao',
  ),
];

export function syncVanHoaCauHinhEmailMenuKeys(
  pathname: string,
  setSelectedKeys: (keys: string[]) => void,
  setOpenKeys: (keys: string[]) => void,
): boolean {
  if (!pathname.startsWith(`${VH_ROUTE_PREFIX}/cau-hinh-email`)) return false;

  if (Object.keys(vanHoaEmailRouteMap).length === 0) {
    buildVanHoaCauHinhEmailChildren();
  }

  const match = vanHoaEmailRouteMap[pathname];
  if (match) {
    setSelectedKeys(match.selected);
    setOpenKeys(match.open);
    return true;
  }

  if (pathname.startsWith(`${VH_ROUTE_PREFIX}/cau-hinh-email/mau-mail/`)) {
    setSelectedKeys(['email-template']);
    setOpenKeys(['sub8']);
    return true;
  }

  return false;
}
